import { useMutation, useQuery } from 'react-query'
import { castImmutable } from 'immer'
import apiClient from '../../utils/api'
import { useGTQueryClient } from '../queryUtils'

export interface TCalendarPlanRequest {
    date: string
    timezone: string
    start_time: string
    end_time: string
    account_id: string
    calendar_id: string
}

export interface TCalendarPlanScore {
    priority: number
    deadline: number
    goal: number
    total: number
}

export interface TCalendarPlanAction {
    id: string
    type: 'add' | 'move'
    task_id: string
    event_id?: string
    title: string
    start: string
    end: string
    original_start?: string
    original_end?: string
    reasons: string[]
    score: TCalendarPlanScore
}

export interface TCalendarPlanUnscheduled {
    task_id: string
    title: string
    reasons: string[]
    score: TCalendarPlanScore
}

export interface TCalendarPlan {
    fingerprint: string
    date: string
    timezone: string
    start: string
    end: string
    actions: TCalendarPlanAction[]
    unscheduled: TCalendarPlanUnscheduled[]
    pending?: boolean
    request?: TCalendarPlanRequest
}

export const useCalendarPlanPreview = (request: TCalendarPlanRequest | undefined, enabled: boolean) =>
    useQuery<TCalendarPlan>(
        ['calendar-plan', request],
        async () => {
            const response = await apiClient.post('/calendar_plans/preview/', request)
            return castImmutable(response.data)
        },
        { enabled: enabled && Boolean(request), staleTime: 60_000, retry: false }
    )

export const useConfirmCalendarPlan = () => {
    const queryClient = useGTQueryClient()
    return useMutation(
        async ({ request, fingerprint }: { request: TCalendarPlanRequest; fingerprint: string }) => {
            const response = await apiClient.post('/calendar_plans/confirm/', { request, fingerprint })
            return response.data
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('events')
                queryClient.invalidateQueries('calendar-plan')
            },
        }
    )
}

export const useCancelCalendarPlan = () => {
    const queryClient = useGTQueryClient()
    return useMutation(
        async ({ fingerprint }: { fingerprint: string }) => {
            const response = await apiClient.post('/calendar_plans/cancel/', { fingerprint })
            return response.data
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('events')
                queryClient.invalidateQueries('calendar-plan')
            },
        }
    )
}
